import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type {
	MultiSelectChangeEvent,
	MultiSelectFilterChangeEvent,
} from "@tql/ui-components-react/kendo/dropdowns";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { MultiSelect } from "@tql/ui-components-react/tql/dropdowns";
import {
	plusCircleLine,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type {
	NotificationType,
	UnifiedIndividualNotificationRecipient,
} from "~/shared/schemas/shipment-notification-schema.ts";
import { NotificationRecipientLevel } from "~/shared/schemas/shipment-notification-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import {
	useNotificationRecipients,
	useSaveIndividualRecipients,
} from "../../hooks/use-notification-recipients.ts";
import { useNotificationTypes } from "../../hooks/use-notification-types.ts";
import {
	isValidEmail,
	MAX_EMAIL_LENGTH,
} from "../../utils/notification-recipient-validation.ts";
import styles from "./shipment-notification-modal.module.css";

type RecipientGroup = {
	customerTypeIds: Set<number>;
	shipmentTypeIds: Set<number>;
	id: string;
	isCustomerLevel: boolean;
};

function groupRecipientsByEmail(
	recipients: readonly UnifiedIndividualNotificationRecipient[],
): Map<string, RecipientGroup> {
	const byEmail = new Map<string, RecipientGroup>();
	for (const r of recipients) {
		const key = r.email.toLowerCase();
		let entry = byEmail.get(key);
		if (!entry) {
			entry = {
				customerTypeIds: new Set<number>(),
				shipmentTypeIds: new Set<number>(),
				id: "",
				isCustomerLevel: false,
			};
			byEmail.set(key, entry);
		}
		if (r.level === NotificationRecipientLevel.Customer) {
			entry.isCustomerLevel = true;
			for (const tid of r.subscribedNotificationTypeIds) {
				entry.customerTypeIds.add(tid);
			}
		} else {
			entry.id = r.id;
			for (const tid of r.subscribedNotificationTypeIds) {
				entry.shipmentTypeIds.add(tid);
			}
		}
	}
	return byEmail;
}

type SubscriberRow = {
	id: string;
	email: string;
	selectAll: boolean;
	subscriptions: Record<number, boolean>;
	isCustomerLevel: boolean;
	customerLevelTypeIds: ReadonlySet<number>;
};

type ShipmentNotificationModalProps = {
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly customerId: number;
	readonly poNumber: number;
};

export function ShipmentNotificationModal({
	isOpen,
	onClose,
	customerId,
	poNumber,
}: ShipmentNotificationModalProps) {
	const { email: currentUserEmail } = useIsAuthenticated();
	const { data: notificationTypes, isLoading: isLoadingTypes } =
		useNotificationTypes();
	const {
		isLoading: isLoadingRecipients,
		isFetching: isFetchingRecipients,
		refetch: refetchRecipients,
	} = useNotificationRecipients(customerId, poNumber);
	const isLoading =
		isLoadingTypes || isLoadingRecipients || isFetchingRecipients;
	const saveMutation = useSaveIndividualRecipients(customerId, poNumber);
	const [emailValues, setEmailValues] = useState<string[]>([]);
	const [filterValue, setFilterValue] = useState("");
	const [subscribers, setSubscribers] = useState<SubscriberRow[]>([]);
	const hasSeededRef = useRef(false);
	const [emailError, setEmailError] = useState<string>("");

	const typeIds = useMemo(
		() => notificationTypes?.map((t) => t.id) ?? [],
		[notificationTypes],
	);

	const seedSubscribers = useCallback(
		(list: readonly UnifiedIndividualNotificationRecipient[]) => {
			const byEmail = groupRecipientsByEmail(list);

			const rows: SubscriberRow[] = [...byEmail.entries()]
				.map(([email, entry]) => {
					const subscriptions: Record<number, boolean> = Object.fromEntries(
						typeIds.map((id) => [
							id,
							entry.customerTypeIds.has(id) || entry.shipmentTypeIds.has(id),
						]),
					);
					const allChecked = typeIds.every((id) => subscriptions[id]);
					return {
						id: entry.id,
						email:
							list.find((r) => r.email.toLowerCase() === email)?.email ?? email,
						selectAll: allChecked,
						subscriptions,
						isCustomerLevel: entry.isCustomerLevel,
						customerLevelTypeIds: entry.customerTypeIds,
					};
				})
				.sort((a, b) => a.email.localeCompare(b.email));
			setSubscribers(rows);

			// Auto-populate the email field if the current user is not already
			// subscribed; otherwise start from a clean input on each open.
			if (
				currentUserEmail &&
				!rows.some(
					(r) => r.email.toLowerCase() === currentUserEmail.toLowerCase(),
				)
			) {
				setEmailValues([currentUserEmail]);
			} else {
				setEmailValues([]);
			}
		},
		[typeIds, currentUserEmail],
	);

	// Each time the modal opens, repull the latest recipients and seed the grid
	// from that fresh result. Seeding from the refetch promise (rather than the
	// cached query value) makes the open deterministic: the grid can never
	// reflect a stale snapshot taken before the refetch settles — e.g. after
	// saving and quickly reopening the modal. A ref (not state) guards the
	// once-per-open seed so claiming the cycle can't retrigger this effect and
	// cancel its own in-flight refetch.
	useEffect(() => {
		if (!isOpen) {
			hasSeededRef.current = false;
			return;
		}
		if (
			hasSeededRef.current ||
			typeIds.length === 0 ||
			customerId <= 0 ||
			poNumber <= 0
		) {
			return;
		}

		hasSeededRef.current = true;

		let cancelled = false;
		void refetchRecipients().then((result) => {
			if (!cancelled && result.data) {
				seedSubscribers(result.data);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [
		isOpen,
		typeIds,
		customerId,
		poNumber,
		refetchRecipients,
		seedSubscribers,
	]);

	const makeEmptySubscriptions = useCallback(
		(): Record<number, boolean> =>
			Object.fromEntries(typeIds.map((id) => [id, false])),
		[typeIds],
	);

	const handleAddToSubscriptionList = () => {
		const pendingFilter = filterValue.trim();
		const allEmails = pendingFilter
			? [...emailValues, pendingFilter]
			: emailValues;

		if (allEmails.length === 0) return;

		const tooLongEmails = allEmails.filter((e) => e.length > MAX_EMAIL_LENGTH);
		if (tooLongEmails.length > 0) {
			setEmailError("Email must be 255 characters or fewer.");
			return;
		}

		const invalidEmails = allEmails.filter((e) => !isValidEmail(e));
		if (invalidEmails.length > 0) {
			setEmailError(
				`Invalid email address${invalidEmails.length > 1 ? "es" : ""}: ${invalidEmails.join(", ")}`,
			);
			return;
		}
		setEmailError("");

		// Deduplicate within the batch
		const uniqueNewEmails = [
			...new Map(allEmails.map((e) => [e.toLowerCase(), e])).values(),
		];

		const existingEmails = new Set(
			subscribers.map((s) => s.email.toLowerCase()),
		);

		const duplicates = uniqueNewEmails.filter((email) =>
			existingEmails.has(email.toLowerCase()),
		);
		if (duplicates.length > 0) {
			setEmailError(`Already added: ${duplicates.join(", ")}`);
			return;
		}

		const newRows: SubscriberRow[] = uniqueNewEmails.map((email) => ({
			id: "",
			email,
			selectAll: false,
			subscriptions: makeEmptySubscriptions(),
			isCustomerLevel: false,
			customerLevelTypeIds: new Set<number>(),
		}));

		if (newRows.length > 0) {
			setSubscribers((prev) => [...prev, ...newRows]);
		}
		setEmailValues([]);
		setFilterValue("");
	};

	const handleToggleType = useCallback(
		(email: string, typeId: number) => {
			setSubscribers((prev) =>
				prev.map((row) => {
					if (row.email !== email || row.customerLevelTypeIds.has(typeId))
						return row;
					const updated = {
						...row.subscriptions,
						[typeId]: !row.subscriptions[typeId],
					};
					const allChecked = typeIds.every((id) => updated[id]);
					return { ...row, subscriptions: updated, selectAll: allChecked };
				}),
			);
		},
		[typeIds],
	);

	const handleToggleSelectAll = useCallback(
		(email: string) => {
			setSubscribers((prev) =>
				prev.map((row) => {
					if (row.email !== email) return row;
					if (typeIds.every((id) => row.customerLevelTypeIds.has(id)))
						return row;
					const newVal = !row.selectAll;
					const subscriptions: Record<number, boolean> = Object.fromEntries(
						typeIds.map((id) => [
							id,
							row.customerLevelTypeIds.has(id)
								? (row.subscriptions[id] ?? false)
								: newVal,
						]),
					);
					const allChecked = typeIds.every((id) => subscriptions[id]);
					return { ...row, selectAll: allChecked, subscriptions };
				}),
			);
		},
		[typeIds],
	);

	const handleRemoveSubscriber = useCallback((email: string) => {
		setSubscribers((prev) => prev.filter((row) => row.email !== email));
	}, []);

	const handleSubscribe = () => {
		const shipmentRecipients = subscribers
			.filter((row) => {
				if (!row.isCustomerLevel) return true;
				return typeIds.some(
					(id) => row.subscriptions[id] && !row.customerLevelTypeIds.has(id),
				);
			})
			.map((row) => ({
				id: row.isCustomerLevel || row.id === "" ? null : row.id,
				firstName: "",
				lastName: "",
				email: row.email,
				notificationTypeIds: typeIds.filter(
					(id) => row.subscriptions[id] && !row.customerLevelTypeIds.has(id),
				),
			}));

		saveMutation.mutate(
			{ poNumber, customerId, recipients: shipmentRecipients },
			{ onSuccess: onClose },
		);
	};

	const columns = useMemo((): BoardColumn[] => {
		const emailCol: BoardColumn = {
			field: "email",
			title: "Email",
			show: true,
			filterable: false,
			sortable: false,
			editable: false,
			width: 200,
		};

		const selectAllCol: BoardColumn = {
			field: "selectAll",
			title: "Select all",
			show: true,
			filterable: false,
			sortable: false,
			width: 90,
			cells: {
				data: (props: GridCustomCellProps) => {
					const row = props.dataItem as SubscriberRow;
					const allCustomerLevel = typeIds.every((id) =>
						row.customerLevelTypeIds.has(id),
					);
					return (
						<GridTd tdProps={props.tdProps}>
							<Checkbox
								disabled={allCustomerLevel}
								onChange={() => {
									handleToggleSelectAll(row.email);
								}}
								value={row.selectAll}
							/>
						</GridTd>
					);
				},
			},
		};

		const typeCols: BoardColumn[] = (notificationTypes ?? []).map(
			(nt: NotificationType) => ({
				field: `type_${String(nt.id)}`,
				title: nt.typeName.replace("Shipment", "").trim(),
				show: true,
				filterable: false,
				sortable: false,
				width: Math.max(
					nt.typeName.replace("Shipment", "").trim().length * 7 + 10,
					80,
				),
				cells: {
					data: (props: GridCustomCellProps) => {
						const row = props.dataItem as SubscriberRow;
						return (
							<GridTd tdProps={props.tdProps}>
								<Checkbox
									disabled={row.customerLevelTypeIds.has(nt.id)}
									onChange={() => {
										handleToggleType(row.email, nt.id);
									}}
									value={row.subscriptions[nt.id] ?? false}
								/>
							</GridTd>
						);
					},
				},
			}),
		);

		const actionsCol: BoardColumn = {
			field: "actions",
			title: "Actions",
			show: true,
			filterable: false,
			sortable: false,
			width: 80,
			cells: {
				data: (props: GridCustomCellProps) => {
					const row = props.dataItem as SubscriberRow;
					return (
						<GridTd tdProps={props.tdProps}>
							<div className={styles.actionsCell}>
								{row.isCustomerLevel ? null : (
									<Button
										onClick={() => {
											handleRemoveSubscriber(row.email);
										}}
										svgIcon={trash_01}
										variant={"dangerTertiary"}
									/>
								)}
							</div>
						</GridTd>
					);
				},
			},
		};

		return [emailCol, selectAllCol, ...typeCols, actionsCol];
	}, [
		notificationTypes,
		handleToggleSelectAll,
		handleToggleType,
		handleRemoveSubscriber,
		typeIds,
	]);

	if (!isOpen) return null;

	return (
		<TraxModal
			className={styles.shipmentNotificationModal}
			onClose={onClose}
			title={"LTL Shipment Notifications"}
			width={"50%"}
		>
			<div className={styles.description}>
				<ul>
					<li>
						Add an email address and click Add Subscriber to opt into shipment
						notifications.
					</li>
					<li>
						Select which notifications you would like to receive by email.
					</li>
					<li>
						Existing subscribers are already set to receive notifications for
						all shipments on this account. Their current event notifications
						can&apos;t be removed, but you may add additional event
						notifications if needed.
					</li>
				</ul>
			</div>

			<div className={styles.emailInputSection}>
				<span className={styles.emailInputLabel}>
					Enter the emails of new subscribers for this shipment:
				</span>
				<div className={styles.emailInputRow}>
					<MultiSelect
						allowCustom={true}
						autoClose={false}
						data={[]}
						filter={filterValue}
						filterable={true}
						listNoDataRender={() => null}
						onChange={(e: MultiSelectChangeEvent) => {
							setEmailValues(e.value as string[]);
							setFilterValue("");
							setEmailError("");
						}}
						onFilterChange={(e: MultiSelectFilterChangeEvent) => {
							setFilterValue(e.filter.value as string);
						}}
						placeholder={"Type an email and press Enter"}
						style={{ flex: 1, maxWidth: 500 }}
						tags={emailValues.map((email) => ({
							text: email,
							data: [email],
						}))}
						value={emailValues}
					/>
					<Button
						disabled={emailValues.length === 0 && !filterValue.trim()}
						onClick={handleAddToSubscriptionList}
						svgIcon={plusCircleLine}
						variant={"primary"}
					>
						Add Subscriber
					</Button>
				</div>
				{emailError ? (
					<span className={styles.emailError}>{emailError}</span>
				) : null}
			</div>

			<div className={styles.subscribersLabel}>
				Shipment Notification Subscribers:
			</div>

			<div className={styles.gridWrapper}>
				<TraxGrid
					columns={convertToGridColumnProps(columns)}
					data={subscribers}
					dataItemKey={"email"}
					loading={isLoading}
					loadingRows={5}
				/>
			</div>

			<DialogActionsBar layout={"end"}>
				<Button
					disabled={saveMutation.isPending}
					onClick={handleSubscribe}
					variant={"primary"}
				>
					Save Preferences
				</Button>
				<Button onClick={onClose} variant={"tertiary"}>
					Cancel
				</Button>
			</DialogActionsBar>
			<LoadingSpinner isVisible={saveMutation.isPending} />
		</TraxModal>
	);
}
